/* SpawnAnything — battle sound director: turns worker frame events into a few well-placed voices.
   Every event is weighted by its distance to the camera and panned by its screen x, then aggregated per category and
   frame (mixer.js): at most a couple of voices per category, louder when many fire at once, plus a crowd/battle bed and
   procedural music whose layers follow the fighting intensity. Slow motion pitches everything down. */
import { createAudioEngine } from './engine.js';
import { createMusic } from './music.js';
import { Aggregator, distGain, screenPan, smooth, rateToIntensity, slowMoRate } from './mixer.js';
import { weaponSound, kindSound } from '../fx/weaponKinds.js';

/** aggregation categories: max voices per frame, min seconds between triggers, base gain, weight as "fighting" */
// tuned 01.10. after "spammen extrem laut": one voice per category, 4-8 triggers per second at most, quieter
// weapons; explosions keep their punch. The battle should sound like a crowd, not like every single swing.
export const CATS = {
  clash: { max: 1, cd: 0.16, gain: 0.32 }, punch: { max: 1, cd: 0.18, gain: 0.32 }, bonk: { max: 1, cd: 0.2, gain: 0.3 },
  zap: { max: 1, cd: 0.18, gain: 0.22 }, flame: { max: 1, cd: 0.3, gain: 0.3 }, whoosh: { max: 1, cd: 0.25, gain: 0.18 }, slam: { max: 1, cd: 0.25, gain: 0.6 },
  rifle: { max: 1, cd: 0.14, gain: 0.26 }, pistol: { max: 1, cd: 0.16, gain: 0.2 }, musket: { max: 1, cd: 0.2, gain: 0.3 }, mg: { max: 1, cd: 0.12, gain: 0.2 },
  laser: { max: 1, cd: 0.14, gain: 0.18 }, railgun: { max: 1, cd: 0.3, gain: 0.4 }, plasma: { max: 1, cd: 0.18, gain: 0.26 },
  bow: { max: 1, cd: 0.16, gain: 0.26 }, throw: { max: 1, cd: 0.2, gain: 0.22 }, rocket: { max: 1, cd: 0.2, gain: 0.36 }, cannon: { max: 1, cd: 0.22, gain: 0.5 },
  honk: { max: 1, cd: 0.25, gain: 0.2 }, gasshot: { max: 1, cd: 0.3, gain: 0.2 },
  expl_small: { max: 2, cd: 0.12, gain: 0.6, act: 2 }, expl_big: { max: 1, cd: 0.25, gain: 0.85, act: 4 }, gas: { max: 1, cd: 0.3, gain: 0.35, act: 2 },
  poof: { max: 1, cd: 0.16, gain: 0.25, act: 2 }, bhit: { max: 1, cd: 0.2, gain: 0.32 }, spawn: { max: 1, cd: 0.3, gain: 0.2, act: 0 },
};
/** category -> [buffer, playback rate] */
export const VOICE = {
  zap: ['laser', 0.55], slam: ['punch', 0.5], throw: ['whoosh', 0.85], gasshot: ['gas', 1.4],
};
const EV_DEATH = 1, EV_EXPL = 2, EV_TRACER = 3, EV_SWING = 4, EV_MUZZLE = 5, EV_SLAM = 6, EV_HIT = 7, EV_BDEAD = 8, EV_SPAWN = 9;
const IMPACT = 0.42; // the sim resolves a melee hit at 42 % of the attack animation

/**
 * deps: { camera (three.js camera), weapons (WEAPONS), fxHints (ERA_WEAPON_FX), armies: () => [A0, A1] }
 */
export function createBattleAudio({ camera, weapons, fxHints = {}, armies }) {
  const engine = createAudioEngine();
  const music = createMusic(engine);
  const agg = new Aggregator(CATS);
  const vp = new Float64Array(16), act = { all: 0, near: 0 };
  let ref = 30, rate = 1, battleRate = 0, nearRate = 0, bed = null, stingerUntil = 0, lastTeam = -1, wasOver = false;
  let cache = [];
  const army = []; // per unit group (weapon events carry side + 2 * group, see sim.worker.js evTag)
  let prevHit = new Float32Array(0);

  function syncArmies() {
    const As = armies(); if (!As || !As[0]) return false;
    if (As.length === cache.length && As.every((A, g) => A === cache[g])) return true;
    cache = As.slice(); army.length = 0;
    for (let t = 0; t < As.length; t++) {
      army[t] = {};
      const A = As[t], key = A.cfg.weapon, wp = A.wp || weapons[key], s = weaponSound(key, wp, fxHints[key]), sc = A.stats.s || 1;
      Object.assign(army[t], { fire: s.fire, melee: s.melee, area: !!A.stats.area, rate: Math.max(0.5, Math.min(1.15, 1 / Math.sqrt(sc))), proj: wp && !wp.melee && !wp.hitscan ? wp.proj : -1, hitscan: !!(wp && wp.hitscan) });
    }
    return true;
  }
  function add(cat, x, y, z, r = 1) {
    const p = camera.position, d = Math.hypot(x - p.x, y - p.y, z - p.z);
    agg.add(cat, distGain(d, ref), screenPan(vp, x, y, z), r);
  }
  /** a direct (non-aggregated) important sound placed in the world */
  function at(name, x, y, z, gain, r = 1, minGain = 0) {
    const p = camera.position, d = Math.hypot(x - p.x, y - p.y, z - p.z);
    engine.play(name, { gain: gain * Math.max(minGain, distGain(d, ref)), pan: screenPan(vp, x, y, z), rate: r * rate, prio: 1 });
  }

  const api = {
    engine, music,
    /** one worker event (ev = Float32Array, o = offset of the 8-float record) */
    event(ev, o) {
      if (!engine.ready || !syncArmies()) return;
      const type = ev[o];
      if (type === EV_DEATH) add('poof', ev[o + 2], ev[o + 3], ev[o + 4]);
      else if (type === EV_EXPL) {
        const kind = ev[o + 5], aoe = ev[o + 4];
        if (kind === 3) at('nuke', ev[o + 1], ev[o + 2], ev[o + 3], 1.1, 1, 0.55);
        else if (kind === 4) add('gas', ev[o + 1], ev[o + 2], ev[o + 3], aoe > 8 ? 0.8 : 1);
        else add(kind === 1 || aoe >= 6 ? 'expl_big' : 'expl_small', ev[o + 1], ev[o + 2], ev[o + 3], kind === 2 ? 1.3 : aoe < 3.5 ? 1.15 : 1);
      } else if (type === EV_TRACER) {
        const a = army[(ev[o + 7] | 0) >> 1];
        add(a && a.hitscan && a.fire ? a.fire : 'rifle', ev[o + 1], ev[o + 2], ev[o + 3]);
      } else if (type === EV_MUZZLE) {
        const kind = ev[o + 4], a = army[(ev[o + 5] | 0) >> 1];
        add(a && a.proj === kind && a.fire ? a.fire : kindSound(kind), ev[o + 1], ev[o + 2], ev[o + 3]);
      } else if (type === EV_SWING) {
        const a = army[(ev[o + 6] | 0) >> 1], cleave = ev[o + 7] > 0;
        if (a && a.melee) add(a.melee, ev[o + 1], ev[o + 5], ev[o + 2], a.rate);
        if (cleave && a && a.melee !== 'flame') add('whoosh', ev[o + 1], ev[o + 5], ev[o + 2], a.rate);
      } else if (type === EV_SLAM) add('slam', ev[o + 1], 0, ev[o + 2]);
      else if (type === EV_HIT) engine.play('punch', { gain: 0.22, rate: 0.85 * rate, prio: 1 });
      else if (type === EV_BDEAD) at('collapse', ev[o + 2], ev[o + 3], ev[o + 4], 1, 1, 0.25);
      else if (type === EV_SPAWN) add('spawn', ev[o + 1], 0, ev[o + 2]);
    },
    /** worker frame: detect melee impacts of non-area armies from the attack phase (those hits send no event) */
    scanMelee(prevB, curB, N) {
      if (!engine.ready || !prevB || !curB || prevB === curB || prevB.length !== curB.length || !syncArmies()) return;
      for (let t = 0; t < army.length; t++) {
        const a = army[t]; if (!a.melee || a.area) continue;
        const i0 = cache[t].start | 0, i1 = Math.min(cache[t].end | 0, N, curB.length / 10);
        for (let i = i0; i < i1; i++) {
          const o = i * 10 + 7, c = curB[o], p = prevB[o];
          if (c >= IMPACT && p < IMPACT && c < IMPACT + 0.4 && curB[o + 2]) add(a.melee, curB[o - 7], curB[o - 6], curB[o - 5], a.rate);
        }
      }
    },
    /** building state from the worker (bs = [hp, alive, level, hit] each): a rising hit timer is a new hit */
    buildings(bs, list) {
      if (!bs || !list) return;
      const n = list.length;
      if (prevHit.length !== n) prevHit = new Float32Array(n);
      for (let b = 0; b < n && b * 4 + 3 < bs.length; b++) {
        const h = bs[b * 4 + 3];
        if (engine.ready && h > prevHit[b] + 0.02 && bs[b * 4 + 1] > 0) { const d = list[b]; add('bhit', d.x, (d.y || 0) + 1.5, d.z); }
        prevHit[b] = h;
      }
    },
    /** a purely visual impact (artillery barrage shells) */
    impact(x, y, z, aoe) { if (engine.ready) add(aoe >= 6 ? 'expl_big' : 'expl_small', x, y, z); },
    /** tool launched by the player at (x, z) */
    tool(key, x, z) {
      if (!engine.ready) return;
      if (key === 'meteor' || key === 'artillerie') engine.play('whistle', { gain: key === 'meteor' ? 0.55 : 0.4, rate: (key === 'meteor' ? 0.8 : 1.1) * rate, pan: screenPan(vp, x, 0, z), prio: 1 });
      else if (key === 'katapult') { engine.play('twang', { gain: 0.6, rate, prio: 1 }); at('whoosh', x, 0, z, 0.4, 0.7); }
      else if (key === 'orbitallaser') at('beam', x, 0, z, 0.8, 1, 0.5);
      else if (key === 'granate' || key === 'fass') at('whoosh', x, 0, z, 0.3, 1.2);
    },
    /** possession changed: team of the new unit, or -1 for god mode */
    possess(team) {
      if (team >= 0) lastTeam = team;
      engine.play('possess', { gain: 0.45, rate: (team >= 0 ? 1 : 0.72) * rate, prio: 1, variant: 0 });
    },
    /** end of battle: fanfare, or a comic "wah wah" if your side (last possessed team) lost or nobody won */
    victory(winner) {
      const lost = winner < 0 || (lastTeam >= 0 && winner !== lastTeam);
      engine.play(lost ? 'defeat' : 'victory', { gain: 0.7, prio: 1, variant: 0 });
      stingerUntil = engine.now() + 3;
    },
    /**
     * per render frame. st: { state ('setup'|'running'), paused, timeScale, over, camDist }
     */
    update(now, dt, st) {
      // camera matrices from the last render (one frame of lag is inaudible)
      const P = camera.projectionMatrix.elements, V = camera.matrixWorldInverse.elements;
      for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) vp[c * 4 + r] = P[r] * V[c * 4] + P[4 + r] * V[c * 4 + 1] + P[8 + r] * V[c * 4 + 2] + P[12 + r] * V[c * 4 + 3];
      ref = 24 + 0.35 * (st.camDist || 0);
      rate = st.paused ? 1 : slowMoRate(st.timeScale);
      if (!engine.ready) return;
      const t = engine.now();
      const n = agg.flush(t);
      for (let k = 0; k < n; k++) {
        const v = agg.out[k], m = VOICE[v.cat];
        engine.play(m ? m[0] : v.cat, { gain: v.gain, pan: v.pan, rate: (m ? m[1] : 1) * v.rate * rate * (0.94 + Math.random() * 0.12) });
      }
      // fighting intensity: events per second, smoothed
      agg.takeActivity(act);
      const fighting = st.state === 'running' && !st.paused && !st.over;
      battleRate = smooth(battleRate, fighting && dt > 0 ? act.all / dt : 0, dt, 1.6);
      nearRate = smooth(nearRate, fighting && dt > 0 ? act.near / dt : 0, dt, 0.8);
      const I = rateToIntensity(battleRate, 30);
      // ambience bed (created lazily, follows nearby fighting)
      if (!bed) bed = engine.loop('ambience', 'amb');
      if (bed) { bed.setGain(st.state === 'running' && !st.over ? (st.paused ? 0.02 : 0.04 + 0.26 * rateToIntensity(nearRate, 6)) : 0, 0.5); bed.setRate(rate, 0.3); }
      // music: menu in setup and after the battle, battle groove while fighting
      if (st.over && !wasOver) stingerUntil = Math.max(stingerUntil, t + 1.5);
      wasOver = !!st.over;
      music.set(st.state === 'running' && !st.over ? 'battle' : 'menu');
      music.setIntensity(I);
      music.setDuck((st.paused ? 0.5 : 1) * (t < stingerUntil ? 0.15 : 1));
      music.update(dt);
    },
    /** 0..1 battle intensity (for debugging/HUD) */
    get intensity() { return rateToIntensity(battleRate, 30); },
  };
  return api;
}
